import { Injectable } from '@angular/core';
import { FndyObjectService } from '@thinglogix/ngx-foundry-core';
import { Observable, of, combineLatest, throwError } from 'rxjs';
import {
    debounceTime,
    distinctUntilChanged,
    finalize,
    map,
    switchMap,
    tap,
} from 'rxjs/operators';
import { FoundryApiService } from 'src/app/services/foundry-api.service';
import { SearchApiService } from 'src/app/services/search-api.service';
import { AttributeFormatPipe } from 'src/app/shared/attribute-format.pipe';
import { SkuModel } from 'src/app/_models/sku.model';
import { SensorModel } from '../../_models/sensor.model';
import { SensorsQuery } from './sensors.query';
import { SensorsStore } from './sensors.store';

const DEBOUNCE_TIME_MS = 200;

@Injectable({
    providedIn: 'root',
})
export class SensorsService {
    constructor(
        protected store: SensorsStore,
        protected query: SensorsQuery,
        private searchApiService: SearchApiService,
        private objectService: FndyObjectService,
        private attributeFormat: AttributeFormatPipe,
        private foundryApiService: FoundryApiService
    ) {}

    initSearch(): Observable<SensorModel[]> {
        return this.query
            .select((state) => state.ui.searchInput)
            .pipe(
                distinctUntilChanged(),
                debounceTime(DEBOUNCE_TIME_MS),
                switchMap((filter) => {
                    this.store.setLoading(true);
                    if (!filter) {
                        return of([]);
                    }
                    return this.searchApiService.getObjectsByName(
                        filter.trim(),
                        '0e1d22f0-7a22-11ec-bb91-cdba4dbd7b2d',
                        'deviceId'
                    );
                }),
                map((objects) =>
                    objects.map((object) =>
                        this.attributeFormat.transform<SensorModel>(object)
                    )
                ),
                tap((objects) => {
                    this.store.set(objects);
                }),
                finalize(() => {
                    this.store.setLoading(false);
                })
            );
    }

    getAllSensors(): Observable<SensorModel[]> {
        this.store.setLoading(true);
        return this.objectService
            .getObjectsByType('0e1d22f0-7a22-11ec-bb91-cdba4dbd7b2d')
            .pipe(
                map((objects) =>
                    objects.map(
                        (object) =>
                            this.attributeFormat.transform<SensorModel>(object),
                        console.log(
                            '0e1d22f0-7a22-11ec-bb91-cdba4dbd7b2d',
                            objects
                        )
                    )
                ),
                tap((objects) => {
                    this.store.set(objects);
                }),
                finalize(() => {
                    this.store.setLoading(false);
                })
            );
    }

    getAssetSensor(id: string): void {
        this.store.setLoading(true);
        this.objectService
            .getObject(id)
            .pipe(
                map((object) =>
                    this.attributeFormat.transform<SensorModel>(object)
                ),
                finalize(() => {
                    this.store.setLoading(false);
                })
            )
            .subscribe((sensor) => {
                this.store.update({ detail: sensor });
            });
    }

    unpairSensorFromSku(sensor: SensorModel): Observable<any> {
        return this.objectService
            .deleteObjectAttributes(sensor.deviceId, ['a.sku', 'a.sku_name'])
            .pipe(
                tap(() => {
                    this.store.update(sensor.deviceId, (sensorToUpdate) => {
                        const attributes = { ...sensorToUpdate.attributes };
                        delete attributes.sku;
                        delete attributes.sku_name;
                        return {
                            ...sensorToUpdate,
                            attributes,
                        };
                    });
                })
            );
    }

    assignSkuToSensor(
        sensorId: string,
        skuId: string
    ): Observable<SensorModel> {
        const sensor$ = this.objectService
            .getObject(sensorId)
            .pipe(
                map((object) =>
                    this.attributeFormat.transform<SensorModel>(object)
                )
            );
        const sku$ = this.objectService
            .getObject(skuId)
            .pipe(
                map((object) =>
                    this.attributeFormat.transform<SkuModel>(object)
                )
            );
        return combineLatest([sensor$, sku$]).pipe(
            switchMap(([sensor, sku]) => {
                console.log(sensor);
                if (sensor.typeId !== '0e1d22f0-7a22-11ec-bb91-cdba4dbd7b2d') {
                    return throwError('Invalid QR Code');
                }
                if (!sensor.attributes.sku) {
                    return this.objectService
                        .updateObject({
                            deviceId: sensorId,
                            'a.sku': skuId,
                            'a.sku_name': sku.attributes.name,
                        })
                        .pipe(
                            map((object) =>
                                this.attributeFormat.transform<SensorModel>(
                                    object
                                )
                            )
                        );
                }
                return throwError(
                    'SKU is already assigned to another sensor, please unassign first.'
                );
            }),
            tap((sensor) => {
                this.store.update(sensor.deviceId, (sensorToUpdate) => {
                    const attributes = { ...sensorToUpdate.attributes };
                    return {
                        ...sensorToUpdate,
                        attributes: {
                            ...attributes,
                            sku: sensor.deviceId,
                            sku_name: sensor.attributes.sku_name,
                        },
                    };
                });
            })
        );
    }

    clearDetail(): void {
        this.store.update(() => ({
            detail: null,
        }));
    }

    clearError(): void {
        this.store.setError(null);
    }

    updateSearchInput(searchInput: any): void {
        console.log(searchInput);
        this.store.update((state) => ({
            ...state,
            ui: {
                ...state.ui,
                searchInput,
            },
        }));
    }

    updateItemsPerPage(itemsPerPage: number): void {
        this.store.update((state: { ui: any }) => ({
            ui: {
                ...state.ui,
                itemsPerPage,
            },
        }));
    }

    updateCurrentPage(currentPage: number): void {
        this.store.update((state) => ({
            ui: {
                ...state.ui,
                currentPage,
            },
        }));
    }
}
